import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { FADE_UP } from "@/lib/motion";
import { useWallet } from "@/contexts/WalletContext";
import { useDexData } from "@/hooks/useDexData";
import { dexErrorMessage } from "@/lib/dex";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TestTokenNote } from "@/components/common/TestTokenNote";
import { TxLink } from "@/components/exchange/TradeParts";
import { DexSwapPanel } from "@/components/dex/DexSwapPanel";
import { DexAddPanel } from "@/components/dex/DexAddPanel";
import { DexPositions, PairsTable } from "@/components/dex/DexPositions";

export default function DexPage() {
  const { address: account, getProvider, refreshBalances } = useWallet();
  const dex = useDexData();
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("");
  const [lastTx, setLastTx] = useState(null);
  const wallet = { account, eip1193: account ? getProvider() : null };

  const run = async (fn, success) => {
    setBusy(true);
    try {
      setLastTx(await fn(setStep));
      toast.success(success);
      dex.refetchPairs();
      setTimeout(() => refreshBalances(), 1500);
    } catch (err) {
      toast.error(dexErrorMessage(err));
    } finally {
      setBusy(false);
      setStep("");
    }
  };
  const [params] = useSearchParams();
  const presetToken = params.get("token");
  const props = { dex, address: wallet, run, busy, step };

  return (
    <div data-testid="dex-page" className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
      <motion.div {...FADE_UP}>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-3">FSC <span className="text-[#D4AF37]">DEX</span></h1>
        <p className="text-zinc-400 mb-3">Create a pair for any two tokens on FMI Smart Chain, add liquidity to earn 0.2% of every swap, and swap between any listed tokens.</p>
        <TestTokenNote testId="dex-token-note" />
      </motion.div>
      <div className="mt-8 space-y-6">
        <Card className="border-[#2A2411] bg-[#0A0A0A]/80">
          <CardContent className="p-5">
            <Tabs defaultValue={presetToken ? "add" : "swap"}>
              <TabsList className="grid grid-cols-3 bg-[#050505] mb-5">
                <TabsTrigger data-testid="dex-tab-swap" value="swap">Swap</TabsTrigger>
                <TabsTrigger data-testid="dex-tab-add" value="add">Add liquidity</TabsTrigger>
                <TabsTrigger data-testid="dex-tab-positions" value="positions">My liquidity</TabsTrigger>
              </TabsList>
              <TabsContent value="swap"><DexSwapPanel {...props} /></TabsContent>
              <TabsContent value="add"><DexAddPanel {...props} presetToken={presetToken} /></TabsContent>
              <TabsContent value="positions"><DexPositions {...props} /></TabsContent>
            </Tabs>
            <TxLink hash={lastTx} testId="dex-tx-link" label="View transaction" receipt={false} />
          </CardContent>
        </Card>
        <PairsTable pairs={dex.pairs} />
        <p className="text-xs text-zinc-600">
          Router <span data-testid="dex-router-address" className="font-mono text-zinc-400 break-all">{dex.router}</span>. Pairs follow the constant-product (V2) model. Your share can be worth more or less than what you deposited as prices move.
        </p>
      </div>
    </div>
  );
}
